import React from 'react';
import { useErrorHandler } from '~/hooks/useErrorHandler';
import { useAppProcessing } from '~/pages/ApplicationDetails/context';
import { DocumentCheckOutlineIcon, ArrowRightOutlineIcon, ArrowLeftOutlineIcon } from '@applyboard/ui-icons';
import {
  Dialog,
  Button,
  Flex,
  Text,
  ButtonLink,
  CheckboxGroup,
  Spinner,
  TextInput,
  Textarea,
  useToasts,
} from '@applyboard/crystal-ui';

import { useApplicationAdmissionCondition } from '../../../data/useAdmissionConditions';
import { useIssuePreOfferActivity } from './useIssuePreOfferActivity';
import { useDeferralRequestActivity } from '../DeferralRequest/useDeferralRequestActivity';
import { DialogHeader, STEPS } from '../Decline/DialogHeading';

export function PreOfferDialog({
  applicationId,
  patchDeferralRequest,
}: {
  applicationId: string;
  patchDeferralRequest?: boolean;
}) {
  const { apiError } = useErrorHandler();
  const { completeProcess, application, startProcess } = useAppProcessing();
  const [loading, setLoading] = React.useState(false);
  const [downloadUrl, setDownloadUrl] = React.useState('');
  const [conditions, setConditions] = React.useState<Array<string>>(
    application.applicationAdmissionConditions.filter((c) => c.code !== 'other').map((c) => c.code)
  );
  const [customCondition, setCustomCondition] = React.useState(
    application.applicationAdmissionConditions.find((c) => c.code === 'other')?.name || ''
  );
  const [additionalNotes, setAdditionalNotes] = React.useState('');
  const [stepState, setStepState] = React.useState<number>(STEPS.STEP1);
  const activity = useIssuePreOfferActivity(applicationId);
  const deferralRequestActivity = useDeferralRequestActivity();
  const toasts = useToasts();

  function onOpenChange(open: boolean) {
    if (!open) {
      setStepState(STEPS.STEP1);
      setCustomCondition('');
      setConditions([]);
      completeProcess();
      setAdditionalNotes('');
    }
  }

  async function onSubmit() {
    setLoading(true);
    if (patchDeferralRequest)
      deferralRequestActivity
        .processDeferralRequest({
          accept: true,
          conditions,
          customCondition,
          additionalNotes: additionalNotes ? additionalNotes : undefined,
        })
        .then(() => {
          toasts.positive('The application intake has been updated and a new pre-offer letter has been issued.');
          deferralRequestActivity.completeProcess();
        })
        .catch(() => apiError())
        .finally(() => setLoading(false));
    else
      activity
        .issuePreOffer(conditions, customCondition, additionalNotes ? additionalNotes : undefined)
        .then((hasInitialOfferActions) => {
          if (hasInitialOfferActions) {
            startProcess('additionalDocumentRequest');
          } else {
            completeProcess();
          }
          toasts.positive('Successfully sent pre-offer letter. It will be available in details tab shortly.');
        })
        .catch(() => apiError())
        .finally(() => setLoading(false));
  }

  async function onButtonClick1() {
    setLoading(true);
    activity
      .generatePreOffer({
        conditions,
        customCondition,
        deferralRequestId: patchDeferralRequest ? application.meta.deferralRequest!.id : undefined,
        additionalNotes: additionalNotes ? additionalNotes : undefined,
      })
      .then(({ letterPreviewDownloadUrl }) => {
        setDownloadUrl(letterPreviewDownloadUrl);
        setStepState(STEPS.STEP2);
      })
      .catch(() => apiError())
      .finally(() => {
        setLoading(false);
      });
  }

  return (
    <Dialog onOpenChange={onOpenChange} size="lg" open={true}>
      <Dialog.Heading>
        <Flex direction="row" gap={2}>
          {patchDeferralRequest && stepState === STEPS.STEP1 && (
            <Button
              aria-label="backButton"
              intent="secondary"
              emphasis="highlighted"
              leadIcon={ArrowLeftOutlineIcon}
              onClick={() => startProcess('deferralRequest')}
            />
          )}
          <DialogHeader setStepState={setStepState} stepState={stepState} heading="Issue Pre-offer" />
        </Flex>
      </Dialog.Heading>
      <Dialog.Content>
        <Flex direction="column" gap={8}>
          {stepState === STEPS.STEP1 && (
            <AdmissionConditionGroup
              conditions={conditions}
              setConditions={setConditions}
              customCondition={customCondition}
              setCustomCondition={setCustomCondition}
              setAdditionalNotes={setAdditionalNotes}
              additionalNotes={additionalNotes}
            />
          )}
          {stepState === STEPS.STEP2 && (
            <>
              <ButtonLink
                aria-label="Download Pre-offer LOA"
                emphasis="highlighted"
                href={downloadUrl}
                intent="primary"
                size="lg"
                leadIcon={DocumentCheckOutlineIcon}
                width="fill"
                mode="override_user_choice_and_open_in_new_tab"
              >
                Pre-offer LOA
              </ButtonLink>
              <Text variant="bodyM" intent="secondary">
                A preview of the pre-offer letter is available before sending it to the applicant. Once the letter is
                sent, you will not be able to make changes.
              </Text>
            </>
          )}
        </Flex>
      </Dialog.Content>
      <Dialog.Actions>
        <Dialog.CloseButton disabled={loading} width="fill">
          Cancel
        </Dialog.CloseButton>
        {stepState === STEPS.STEP1 && (
          <Button
            intent="primary"
            loading={loading}
            width="fill"
            trailIcon={ArrowRightOutlineIcon}
            onClick={onButtonClick1}
          >
            Next
          </Button>
        )}
        {stepState === STEPS.STEP2 && (
          <Button intent="primary" loading={loading} onClick={onSubmit} width="fill">
            Send Pre-offer
          </Button>
        )}
      </Dialog.Actions>
    </Dialog>
  );
}

function AdmissionConditionGroup(props: {
  setConditions: (conditions: Array<string>) => void;
  conditions: Array<string>;
  setCustomCondition: (condition: string) => void;
  customCondition: string;
  setAdditionalNotes: (notes: string) => void;
  additionalNotes: string;
}) {
  const { admissionConditions, isFetchingAdmissionCondition } = useApplicationAdmissionCondition();

  if (isFetchingAdmissionCondition) {
    return (
      <Flex direction="column" align="center">
        <Flex>
          <Spinner />
        </Flex>
      </Flex>
    );
  }

  return (
    <>
      <CheckboxGroup
        onChange={(conditions: Array<string>) => {
          props.setConditions(conditions);
        }}
        value={props.conditions}
        label="You can optionally choose one or more conditions so the student can be accepted in the program."
      >
        <Flex direction="column">
          {admissionConditions?.map((i) => <CheckboxGroup.Option label={i.name} value={i.code} key={i.id} />)}
        </Flex>
      </CheckboxGroup>
      <TextInput
        label="Custom condition"
        intent="secondary"
        onChange={(condition: string) => props.setCustomCondition(condition)}
        value={props.customCondition}
        helpText="Add any conditions that are not included in the list above"
      />
      <Textarea
        label="Additional Notes"
        intent="secondary"
        onChange={(notes: string) => props.setAdditionalNotes(notes)}
        value={props.additionalNotes}
      />
    </>
  );
}
